import React, { FC } from 'react';
import { Box } from '@mui/material';
import { AdditionalFieldKey, AdditionalFieldsModel, ADDITIONAL_FIELDS_META } from '../../model';
import { agencyDictionary, clientDictionary } from '../../dictionaries/model';
import { DictionaryAutocomplete } from '../DictionaryAutocomplete';
import { AdditionalFieldsSectionProps } from '../../types/additionalFieldsForm';

type DictionaryFieldKey = 'agencyName' | 'clientName';

type AgencyClientSectionProps = AdditionalFieldsSectionProps & {
  setValues: React.Dispatch<React.SetStateAction<AdditionalFieldsModel>>;
  debouncedSaveField: (
    key: AdditionalFieldKey,
    value: AdditionalFieldsModel[AdditionalFieldKey],
  ) => void;
  saveField: (
    key: AdditionalFieldKey,
    valueOverride?: AdditionalFieldsModel[AdditionalFieldKey],
    options?: { addToDictionary?: boolean },
  ) => Promise<void>;
  clearSaveFieldTimeout: (key: string) => void;
  setDictionaryFieldValidity: (key: DictionaryFieldKey, valid: boolean) => void;
};

export const AgencyClientSection: FC<AgencyClientSectionProps> = ({
  values,
  isFieldsDisabled,
  setValues,
  debouncedSaveField,
  saveField,
  clearSaveFieldTimeout,
  setDictionaryFieldValidity,
}) => (
  <Box display="flex" gap={2} flexWrap="wrap" minHeight={110} mb={-2}>
    <Box sx={{ flex: 1, minWidth: 200 }}>
      <DictionaryAutocomplete
        label={ADDITIONAL_FIELDS_META.agencyName.label}
        value={values.agencyName ?? ''}
        disabled={isFieldsDisabled}
        onChange={(newValue, meta) => {
          const current = values.agencyName ?? '';
          setValues((prev) => ({
            ...prev,
            agencyName: newValue,
          }));

          if (meta?.skipAutosave) {
            clearSaveFieldTimeout('agencyName');
            return;
          }

          if (newValue === current) return;
          debouncedSaveField('agencyName', newValue);
        }}
        dictionaryModel={agencyDictionary}
        onValidityChange={(valid) => {
          setDictionaryFieldValidity('agencyName', valid);
        }}
        onSaveAfterAdd={() => {
          saveField('agencyName', undefined, { addToDictionary: false });
        }}
      />
    </Box>

    <Box sx={{ flex: 1, minWidth: 200 }}>
      <DictionaryAutocomplete
        label={ADDITIONAL_FIELDS_META.clientName.label}
        value={values.clientName ?? ''}
        disabled={isFieldsDisabled}
        onChange={(newValue, meta) => {
          const current = values.clientName ?? '';
          setValues((prev) => ({
            ...prev,
            clientName: newValue,
          }));

          if (meta?.skipAutosave) {
            clearSaveFieldTimeout('clientName');
            return;
          }

          if (newValue === current) return;
          debouncedSaveField('clientName', newValue);
        }}
        dictionaryModel={clientDictionary}
        onValidityChange={(valid) => {
          setDictionaryFieldValidity('clientName', valid);
        }}
        onSaveAfterAdd={() => {
          saveField('clientName', undefined, { addToDictionary: false });
        }}
      />
    </Box>
  </Box>
);
